import React, {memo, useCallback, useEffect, useMemo, useState} from 'react'
import {createTable} from '@tanstack/react-table'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {UpdateListType} from '../../../component/type'
import {Button, List as ListView, Popup} from '../../../component'
import ListRight from '../../../component/listRight'
import {
  getCarousalList,
  getCarousalDetail,
  clearCarousalDetail,
  deleteCarousal,
} from '../../../store/actions'
import {useSelector, useAppDispatch} from '../../../store'
import {CarousalType} from '../../../store/actions/types'
import {UpdateButtonList, RemoveButtonList} from '../../../component/List/Commands'
import {defaultPageSize} from '../../../configs/paginationSetting'

const table = createTable().setRowType<CarousalType>()
const CarousalList = memo(() => {
  const {t} = useTranslation()
  const navigation = useNavigate()
  const {list, total, from} = useSelector(({carousal}) => ({
    list: carousal.carousalList,
    total: carousal.total,
    from: carousal.from,
  }))
  const [selectedRow, setSelectedRow] = useState<CarousalType>()
  const [isOpenPopup, setIsOpenPopup] = useState(false)
  const [currentItemId, setCurrentItemId] = useState(0)
  const [loading, setLoading] = useState(false)
  const defaultOptions = useMemo(() => ({page: 1, per_page: defaultPageSize}), [])
  const [options, setOptions] = useState(defaultOptions)
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(getCarousalList(options))
  }, [options])
  const columns = useMemo(
    () => [
      table.createDataColumn('id', {
        header: 'NUMBER',
        size: 100,
        cell: ({row}) => <span>{String(row.index + from).padStart(2, '0')}</span>,
      }),
      table.createDataColumn('medias_model', {
        header: 'IMAGE',
        size: 150,
        cell: ({row}) => (
          <div className="c-news-card__image mr-0 bg-red">
            {row.original?.medias_model &&
              row.original.medias_model?.length > 0 &&
              row.original.medias_model[0]?.original_url && (
                <div
                  style={{
                    backgroundImage: `url(${row.original.medias_model[0].original_url})`,
                  }}
                  className="cover-image"
                />
              )}
          </div>
        ),
      }),
      table.createDataColumn('banner_title', {
        header: 'TITLE',
        size: 250,
        cell: ({row}) => <span>{row.original?.banner_title}</span>,
      }),
      table.createDataColumn('banner_description', {
        header: 'DESCRIPTION',
        size: 500,
        cell: ({row}) => <span>{row.original?.banner_description}</span>,
      }),
      // table.createDataColumn('button_text', {
      //   header: 'BUTTON NAME',
      //   size: 50,
      //   cell: ({row}) => <span>{row.original?.button_text}</span>,
      // }),
      // table.createDataColumn('button_url', {
      //   header: 'BUTTON URL',
      //   size: 100,
      //   cell: ({row}) => <span style={{wordBreak: 'break-word'}}>{row.original?.button_url}</span>,
      // }),
      table.createDataColumn('id', {
        header: '',
        cell: ({row}) =>
          row.original && (
            <ListRight
              row={row.original}
              onClick={onUpdatePress}
              buttonList={[UpdateButtonList, RemoveButtonList]}
            />
          ),
      }),
    ],
    [from],
  )

  const onUpdatePress = useCallback(
    async ({id, command}: UpdateListType) => {
      if (command === 'update') {
        const response = await dispatch(getCarousalDetail(id))
        if (response) navigation(`/content-management/carousal/${id}`)
      } else if (command === 'remove') {
        setCurrentItemId(id)
        setIsOpenPopup(true)
      } else if (command === 'deleteConfirmed') {
        setLoading(true)
        await dispatch(deleteCarousal(id, options))
        setIsOpenPopup(false)
        setLoading(false)
      }
      setSelectedRow(undefined)
    },
    [selectedRow, list],
  )

  const onPaginate = useCallback(
    (page: number) => {
      setOptions({...options, page})
    },
    [options],
  )

  const onLimit = useCallback(
    (per_page: number) => {
      setOptions({...options, per_page, page: 1})
    },
    [options],
  )

  const onAddNewPress = useCallback(() => {
    dispatch(clearCarousalDetail())
    navigation(`/content-management/carousal/add`)
  }, [])

  return (
    <div className="table-outer">
      <div className="table-header">
        <div className="c-carousal__header">
          <div className="c-carousal__header__lpart v-center">
            <span className="f-Exo-Bold title-color">{t('carousal').toUpperCase()}</span>
          </div>
          <div className="c-carousal__header__rpart">
            <Button
              icon="c-icon--plus"
              title={t('addNew')}
              classAddon="c-button--icons f-Exo-Medium"
              onClick={onAddNewPress}
            />
            <Popup
              isOpen={isOpenPopup}
              setIsOpen={setIsOpenPopup}
              onPressDelete={() => onUpdatePress({id: currentItemId, command: 'deleteConfirmed'})}
              title={t('delete')}
              description={t('deleteText')}
              loading={loading}
            />
          </div>
        </div>
      </div>
      <ListView
        columns={columns}
        data={list}
        total={total}
        onPaginate={onPaginate}
        forcePageIndex={options.page - 1}
        options={options}
        isPageSize
        onLimit={onLimit}
        pageSize={options?.per_page}
      />
    </div>
  )
})
export default CarousalList
